import React, { useState } from 'react'
import {
  NewGridTable,
  IGridTableColumn,
  Skeleton,
  IconButton,
  Button,
  Details,
  Toaster,
  Confirmation,
  Dialog,
} from '@ui'
import { FormGroup, InputGroup, Classes, Intent, Tag } from '@blueprintjs/core'
import { NavigateFn } from '@reach/router'
import { UseBuildingTypesHook } from './hooks/useBuildingTypes'
import { IBuildingTypeWithCompany, graphQLError } from '@api'
import api from '@api'
import { useReload } from '@hooks/reloadContext'
import track from '@track'
import Event from '@track/Event'
import css from './tableView.module.scss'

export function handleErrors(errors: { graphQLErrors: graphQLError[] }, returnDivs: boolean) {
  const errorMessage: string[] = []
  if (errors && errors.graphQLErrors?.length) {
    errors.graphQLErrors.forEach((gqlErr) => {
      if (gqlErr.unknownErrors && Object.keys(gqlErr.unknownErrors).length) {
        gqlErr.unknownErrors.forEach((ue) => errorMessage.push(ue.message))
      }
      if (gqlErr.invalidArguments && Object.keys(gqlErr.invalidArguments).length) {
        Object.values(gqlErr.invalidArguments).forEach((ia) => {
          errorMessage.push(ia)
        })
      }
    })
    if (!errorMessage.length)
      for (const { message } of errors.graphQLErrors) {
        errorMessage.push(message)
      }
  }

  if (returnDivs) return errorMessage.map((e, i) => <div key={i}>{e}</div>)
  return errorMessage
}

export default function TableView({
  buildingTypes,
  navigate,
}: {
  buildingTypes: UseBuildingTypesHook
  navigate?: NavigateFn
}) {
  const [showNew, setShowNew] = useState(false)
  const [name, setName] = useState('')
  const [errors, setErrors] = useState<React.ReactNode[]>([])
  const [loading, setLoading] = useState(false)
  const reload = useReload()

  const onRemove = (id: string) =>
    api.building.buildingType
      .remove(id)
      .then(() => {
        reload.buildingTypes()
      })
      .catch((error: { graphQLErrors: graphQLError[] }) => {
        const stringErrors = handleErrors(error, false) as string[]
        stringErrors.forEach((e: string) => Toaster.fail({ message: e }))
      })

  const columns: IGridTableColumn<IBuildingTypeWithCompany>[] = [
    {
      label: 'Name',
      sort: 'displayName',
      Cell: ({ cell, skeleton }) =>
        skeleton ? (
          <Skeleton />
        ) : (
          <div className="flex justify-start items-center" style={{ width: '100%', height: 32 }}>
            <div
              className="table__link"
              onClick={() => {
                navigate?.(`?buildingTypeId=${cell?.data.id}`)
                track.buildingTypes({ eventName: Event.ROW_CLICK })
              }}
            >
              {cell?.data.displayName}
            </div>
            {!cell?.data.company && (
              <Tag minimal className="ml-1">
                Default
              </Tag>
            )}
          </div>
        ),
    },
    {
      label: '',
      Cell: ({ cell }) => {
        if (!cell || !cell.data.company) return null
        return (
          <div className="flex justify-end" style={{ width: '100%' }}>
            <Confirmation
              position="top-right"
              onConfirm={() => onRemove(cell.data.id)}
              cancelable
              text={
                <div style={{ width: 200, whiteSpace: 'normal' }}>Delete this Building Type?</div>
              }
            >
              <IconButton iconName="times" tooltip="Delete" minimal />
            </Confirmation>
          </div>
        )
      },
    },
  ]

  return (
    <>
      <div className={`flex items-center justify-end my-2 ${css.buildingTypeToolbar}`}>
        <Button
          className="ml2"
          text="New Building Type"
          data-button="new_building_type"
          iconName="plus"
          onClick={() => {
            setShowNew(true)
          }}
        />
      </div>
      <NewGridTable<IBuildingTypeWithCompany>
        status={buildingTypes.status}
        columns={columns}
        data={buildingTypes.items}
        page={{
          hasNextPage: buildingTypes.pageInfo.hasNextPage,
          endCursor: buildingTypes.pageInfo.endCursor,
          onNextPage: buildingTypes.nextPage,
        }}
        sort={{
          column: buildingTypes.sort.column,
          order: buildingTypes.sort.order,
          onSort: (column: any) => {
            buildingTypes.sortBy(column)
            track.buildingTypes({
              eventName: Event.SORT,
              props: { columnName: column },
            })
          },
        }}
        headerTop={52}
      />
      <Dialog
        isOpen={showNew}
        title="New Building Type"
        usePortal
        isCloseButtonShown={false}
        toolbar={
          <div className="dialog-buttons">
            <IconButton
              minimal
              tooltip="Close"
              iconName="times"
              onClick={() => setShowNew(false)}
            />
          </div>
        }
        body={
          <Details
            onSubmit={(e) => {
              e.preventDefault()
              setLoading(true)
              api.building.buildingType
                .create(name.trim())
                .then(() => {
                  setName('')
                  setLoading(false)
                  setErrors([])
                  reload.buildingTypes()
                  Toaster.success({ message: 'Building Type Created' })
                  setShowNew(false)
                })
                .catch((error: { graphQLErrors: graphQLError[] }) => {
                  setLoading(false)
                  setErrors(handleErrors(error, true))
                })
            }}
          >
            <div className={Classes.DIALOG_BODY}>
              <Details.Row>
                <Details.Label label="Name" required />
                <Details.Column>
                  <FormGroup helperText={errors} intent={errors.length ? Intent.DANGER : undefined}>
                    <InputGroup
                      autoFocus
                      value={name}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setName(event.target.value)
                      }
                    />
                  </FormGroup>
                </Details.Column>
              </Details.Row>
            </div>
            <div className={Classes.DIALOG_FOOTER}>
              <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                <Button text="Create" intent="primary" type="submit" loading={loading} />
              </div>
            </div>
          </Details>
        }
      />
    </>
  )
}
